import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Modal } from '../../../components/Modal';
import { vmsService } from '../../../services/vmsService';

interface AddEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  vendorId: string | number;
  vendor?: any;
  onSuccess: () => void;
  initialData?: any;
  tabType?: 'invoices' | 'documents';
}

export const AddEntryModal: React.FC<AddEntryModalProps> = ({ isOpen, onClose, vendorId, vendor, onSuccess, initialData, tabType }) => {
  const userDepartment = useSelector((state: RootState) => state.auth.user?.department);
  const isFinanceDept = userDepartment?.toLowerCase() === 'finance';
  const [docTypes, setDocTypes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [dateIssued, setDateIssued] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [receivedByAccountsDate, setReceivedByAccountsDate] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [isGstInclusive, setIsGstInclusive] = useState(false);
  const [cgst, setCgst] = useState('');
  const [sgst, setSgst] = useState('');
  const [igst, setIgst] = useState('');
  const [tds, setTds] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('unpaid');
  const [utrNumber, setUtrNumber] = useState('');
  const [docTypeId, setDocTypeId] = useState('');

  // For creating a new doc type
  const [isNewDocType, setIsNewDocType] = useState(false);
  const [newDocTypeName, setNewDocTypeName] = useState('');
  const [newDocTypeDesc, setNewDocTypeDesc] = useState('');

  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchDocTypes();
      if (initialData) {
        setDateIssued(initialData.date_issued != null ? initialData.date_issued : '');
        setDateTo(initialData.due_date != null ? initialData.due_date : '');
        setReceivedByAccountsDate(initialData.received_by_accounts_date != null ? initialData.received_by_accounts_date : '');
        setInvoiceId(initialData.invoice_id != null ? initialData.invoice_id : '');
        setIsGstInclusive(initialData.is_gst_inclusive ?? false);
        setCgst(initialData.cgst != null ? String(initialData.cgst) : '');
        setSgst(initialData.sgst != null ? String(initialData.sgst) : '');
        setIgst(initialData.igst != null ? String(initialData.igst) : '');
        setTds(initialData.tds != null ? String(initialData.tds) : '');
        setInvoiceAmount(initialData.invoice_amount != null ? String(initialData.invoice_amount) : '');

        setAmountPaid('');
        setPaymentDate('');

        setPaymentStatus(initialData.payment_status != null ? initialData.payment_status : 'unpaid');
        setUtrNumber(initialData.utr_number != null ? initialData.utr_number : '');
        setDocTypeId(initialData.read_doc_type_id != null ? String(initialData.read_doc_type_id) : '');
        setFile(null);
      } else {
        setDateIssued('');
        setDateTo('');
        setReceivedByAccountsDate('');
        setInvoiceId('');
        setIsGstInclusive(false);
        setCgst('');
        setSgst('');
        setIgst('');
        setTds('');
        setInvoiceAmount('');
        setPaymentDate(new Date().toISOString().split('T')[0]);
        setAmountPaid('');
        setPaymentStatus('unpaid');
        setUtrNumber('');
        setDocTypeId('');
        setFile(null);
      }
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    if (invoiceAmount) {
      const inv = parseFloat(invoiceAmount) || 0;
      const prevPaid = initialData?.amount_paid ? parseFloat(initialData.amount_paid) : 0;
      const pd = (parseFloat(amountPaid) || 0) + prevPaid;
      if (pd >= inv) {
        setPaymentStatus('paid');
      } else if (pd > 0) {
        setPaymentStatus('partially_paid');
      } else {
        setPaymentStatus('unpaid');
      }
    }
  }, [invoiceAmount, amountPaid, initialData]);

  const fetchDocTypes = async () => {
    try {
      const types = await vmsService.getDocTypes();
      setDocTypes(types);
    } catch (err) {
      console.error('Failed to fetch doc types', err);
    }
  };

  const filteredDocTypes = docTypes.filter(dt => {
    const isInv = dt.name.toLowerCase().includes('invoice');
    if (tabType === 'invoices') return isInv;
    if (tabType === 'documents') return !isInv;
    return true;
  });

  useEffect(() => {
    if (tabType === 'invoices' && filteredDocTypes.length > 0 && !docTypeId && !initialData) {
      setDocTypeId(String(filteredDocTypes[0].id));
    }
  }, [filteredDocTypes.length, tabType, docTypeId, initialData]);

  const checkIsInvoice = (name: string) => {
    if (!name) return false;
    const lower = name.toLowerCase();
    return lower.includes('invoice');
  };

  const selectedDocType = docTypes.find(dt => String(dt.id) === String(docTypeId));
  const isInvoiceSelected = isNewDocType
    ? checkIsInvoice(newDocTypeName)
    : (selectedDocType ? checkIsInvoice(selectedDocType.name) : (initialData?.doc_type ? checkIsInvoice(initialData.doc_type) : false));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const prevPaid = initialData?.amount_paid ? parseFloat(initialData.amount_paid) : 0;
      const newPaid = amountPaid ? parseFloat(amountPaid) : 0;
      const totalPaid = isInvoiceSelected ? (prevPaid + newPaid) : null;

      const detailData = {
        doc_type_id: docTypeId,
        invoice_id: isInvoiceSelected ? (invoiceId || null) : null,
        date_issued: isInvoiceSelected ? (dateIssued || null) : null,
        due_date: isInvoiceSelected ? (dateTo || null) : null,
        received_by_accounts_date: isInvoiceSelected ? (receivedByAccountsDate || null) : null,
        is_gst_inclusive: isInvoiceSelected ? isGstInclusive : null,
        cgst: (isInvoiceSelected && isGstInclusive) ? (parseFloat(cgst) || 0) : null,
        sgst: (isInvoiceSelected && isGstInclusive) ? (parseFloat(sgst) || 0) : null,
        igst: (isInvoiceSelected && isGstInclusive) ? (parseFloat(igst) || 0) : null,
        tds: (isInvoiceSelected && tds) ? parseFloat(tds) : null,
        invoice_amount: (isInvoiceSelected && invoiceAmount) ? parseFloat(invoiceAmount) : null,
        payment_date: (isInvoiceSelected && paymentDate) ? paymentDate : null,
        amount_paid: totalPaid,
        payment_status: isInvoiceSelected ? paymentStatus : null,
        utr_number: isInvoiceSelected ? utrNumber : null,
      };

      let docDetailId = initialData?.id;
      if (initialData?.id) {
        await vmsService.updateVendorDocDetail(vendorId, initialData.id, detailData);
      } else {
        const docDetail = await vmsService.createVendorDocDetail(vendorId, detailData);
        docDetailId = docDetail.id;
      }

      if ((newPaid > 0 || file) && docDetailId) {
        const formData = new FormData();
        if (file) formData.append('file', file);
        if (newPaid > 0) {
          formData.append('amount_paid', String(newPaid));
          formData.append('payment_date', paymentDate || new Date().toISOString().split('T')[0]);
          if (utrNumber) formData.append('utr_number', utrNumber);
          formData.append('remarks', `Payment of INR ${newPaid} recorded.`);
        }
        await vmsService.uploadVendorDocVersion(vendorId, docDetailId, formData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to save entry', err);
      alert('Failed to save entry. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={initialData ? "Edit Document Entry" : "Add Entry / Upload Document"} maxWidth="1200px" width="90%">
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          <div className="form-row">
            <div className="filter-group form-full">
              <label>Document Type *</label>
              {!isNewDocType ? (
                <select required value={docTypeId} disabled={!!initialData} onChange={e => {
                  if (e.target.value === 'NEW') setIsNewDocType(true);
                  else setDocTypeId(e.target.value);
                }}>
                  <option value="">Select Type</option>
                  {filteredDocTypes.map(dt => (
                    <option key={dt.id} value={dt.id}>{dt.name}</option>
                  ))}
                  {!initialData && <option value="NEW">+ Create New Type</option>}
                </select>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                  <input type="text" placeholder="New Type Name" required value={newDocTypeName} onChange={e => setNewDocTypeName(e.target.value)} />
                  <input type="text" placeholder="Description (optional)" value={newDocTypeDesc} onChange={e => setNewDocTypeDesc(e.target.value)} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <button type="button" onClick={() => setIsNewDocType(false)} style={{ fontSize: '0.85rem', color: 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Cancel</button>
                    <button type="button" onClick={async (e) => {
                      e.preventDefault();
                      if (!newDocTypeName.trim()) return;
                      try {
                        const newType = await vmsService.createDocType({ name: newDocTypeName, description: newDocTypeDesc });
                        const data = await vmsService.getDocTypes();
                        setDocTypes(data);
                        setDocTypeId(String(newType.id));
                        setIsNewDocType(false);
                        setNewDocTypeName('');
                        setNewDocTypeDesc('');
                      } catch (err) {
                        alert('Failed to save new document type');
                      }
                    }} style={{ fontSize: '0.85rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Save</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {isInvoiceSelected && (
            <>
              <div className="form-row">
                <div className="filter-group form-full">
                  <label>Invoice ID (Optional)</label>
                  <input type="text" value={invoiceId} onChange={e => setInvoiceId(e.target.value)} placeholder="e.g. INV-2023-001" />
                </div>
              </div>
              <div className="form-row">
                <div className="filter-group">
                  <label>Issued Date *</label>
                  <input type="date" required value={dateIssued} onChange={e => setDateIssued(e.target.value)} />
                </div>
                <div className="filter-group">
                  <label>Due Date *</label>
                  <input type="date" required value={dateTo} onChange={e => setDateTo(e.target.value)} />
                </div>
                <div className="filter-group">
                  <label>Received Date (Accounts)</label>
                  <input type="date" value={receivedByAccountsDate} onChange={e => setReceivedByAccountsDate(e.target.value)} />
                </div>
              </div>

              <div className="form-row">
                <div className="filter-group form-full">
                  <label>GST Type</label>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="radio" checked={isGstInclusive} onChange={() => setIsGstInclusive(true)} style={{ width: 'auto' }} />
                      Inclusive GST
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="radio" checked={!isGstInclusive} onChange={() => setIsGstInclusive(false)} style={{ width: 'auto' }} />
                      Exclusive GST
                    </label>
                  </div>
                </div>
              </div>

              {isGstInclusive && (
                <div className="form-row">
                  <div className="filter-group">
                    <label>CGST Amount</label>
                    <input type="number" step="0.01" value={cgst} onChange={e => setCgst(e.target.value)} placeholder="e.g. 500" />
                  </div>
                  <div className="filter-group">
                    <label>SGST Amount</label>
                    <input type="number" step="0.01" value={sgst} onChange={e => setSgst(e.target.value)} placeholder="e.g. 500" />
                  </div>
                  <div className="filter-group">
                    <label>IGST Amount</label>
                    <input type="number" step="0.01" value={igst} onChange={e => setIgst(e.target.value)} placeholder="e.g. 500" />
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="filter-group">
                  <label>TDS (Optional)</label>
                  <input type="number" step="0.01" value={tds} onChange={e => setTds(e.target.value)} placeholder="e.g. 100" />
                </div>
              </div>

              <div className="form-row">
                <div className="filter-group">
                  <label>Invoice Amount</label>
                  <input type="number" step="0.01" value={invoiceAmount} onChange={e => setInvoiceAmount(e.target.value)} placeholder="e.g. 5000" />
                </div>
                {isFinanceDept && (
                  <div className="filter-group">
                    <label>
                      New Payment Amount
                      {initialData?.amount_paid ? ` (Already Paid: ₹${initialData.amount_paid}, Remaining: ₹${Math.max(0, (parseFloat(invoiceAmount || '0') - parseFloat(initialData.amount_paid)))})` : ''}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      max={Math.max(0, (parseFloat(invoiceAmount || '0') - (initialData?.amount_paid ? parseFloat(initialData.amount_paid) : 0)))}
                      value={amountPaid}
                      onChange={e => {
                        const val = parseFloat(e.target.value) || 0;
                        const maxVal = Math.max(0, (parseFloat(invoiceAmount || '0') - (initialData?.amount_paid ? parseFloat(initialData.amount_paid) : 0)));
                        if (val > maxVal) {
                          setAmountPaid(String(maxVal));
                        } else {
                          setAmountPaid(e.target.value);
                        }
                      }}
                      placeholder="e.g. 5000"
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                {isFinanceDept && (
                  <div className="filter-group">
                    <label>Payment Date</label>
                    <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
                  </div>
                )}
                <div className="filter-group">
                  <label>Payment Status</label>
                  <input type="text" readOnly disabled className="field-disabled" value={paymentStatus === 'paid' ? 'Fully Paid' : paymentStatus === 'partially_paid' ? 'Partially Paid' : 'Unpaid'} style={{ textTransform: 'capitalize' }} />
                </div>
              </div>
              <div className="form-row">
                <div className="filter-group form-full">
                  <label>UTR Number (Optional)</label>
                  <input type="text" value={utrNumber} onChange={e => setUtrNumber(e.target.value)} placeholder="e.g. UTR123456789" />
                </div>
              </div>
            </>
          )}

          <div className="form-row">
            <div className="filter-group form-full">
              <label>Upload Document</label>
              <input type="file" style={{ padding: '0.4rem', background: '#fff' }} onChange={e => setFile(e.target.files ? e.target.files[0] : null)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="secondary-btn" onClick={onClose} disabled={isLoading}>Cancel</button>
          <button type="submit" className="primary-btn" disabled={isLoading}>
            {isLoading ? 'Saving...' : (initialData ? 'Update Entry' : 'Save Entry')}
          </button>
        </div>
      </form>
    </Modal>
  );
};
